import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { NavLink, Navigate, Route, Routes, useLocation, useNavigate, useParams } from 'react-router-dom'

import BottomNav from './components/common/BottomNav'
import TootiDialog from './components/common/Dialog'
import InstallPrompt from './components/common/InstallPrompt'
import ParrotMark from './components/common/ParrotMark'
import LaunchScene from './components/launch/LaunchScene'
import Spinner from './components/common/Spinner'
import RulesScreen from './components/social/RulesScreen'
import { useAutoPost } from './hooks/useFeatures'
import { rulesView } from './lib/rules'
import StatusBanner from './components/common/StatusBanner'
import ToastBar from './components/common/ToastBar'
import { useOnline } from './hooks/useOnline'
import { useReminderClock } from './hooks/useReminderClock'
import { logoutDialog } from './lib/alerts'
import { hasBeenAsked, hasSeenOnboarding, readPendingAnswers, shouldAskQuestions, startDestination } from './lib/onboarding'
import { launchScenePending } from './lib/launchScene'
import { faDigits } from './lib/numerals'
import { onSubscriptionRequired } from './services/api'
import { fetchGeoLocale } from './services/geo'
import { listNotifications } from './services/notifications'
import SiteShell from './components/site/SiteShell'
import { isSiteMode, postLoginDestination } from './lib/siteMode'
import Account from './pages/Account'
import Alerts from './pages/Alerts'
import Archive from './pages/Archive'
import ArchiveResult from './pages/ArchiveResult'
import Auth from './pages/Auth'
import Contact from './pages/Contact'
import Autopay from './pages/Autopay'
import Download from './pages/Download'
import EditProfile from './pages/EditProfile'
import Home from './pages/Home'
import Landing from './pages/Landing'
import Leaderboard from './pages/Leaderboard'
import Notifications from './pages/Notifications'
import Onboarding from './pages/Onboarding'
import ArcadePath from './pages/ArcadePath'
import Lesson from './pages/Lesson'
import Lessons from './pages/Lessons'
import Paywall from './pages/Paywall'
import Privacy from './pages/Privacy'
import DeleteAccount from './pages/DeleteAccount'
import Profile from './pages/Profile'
import PublicProfile from './pages/PublicProfile'
import Settings from './pages/Settings'
import Shop from './pages/Shop'
import Social from './pages/Social'
import { watchProfileRefresh } from './lib/profileRefresh'
import { useAuthStore } from './store/authStore'
import { useRulesStore } from './store/rulesStore'
import { usePrefsStore } from './store/prefsStore'
import { useThemeStore } from './store/themeStore'
import Admin from './pages/Admin'

/**
 * The caller's country, asked once per app load (backend-nest's
 * `/geo/locale`) and handed to `prefsStore.applyGeo`, which decides what it
 * is worth: a default outside Iran, a lock inside it (lib/geoLocale.js).
 *
 * That lock is why the request is no longer skipped once `languageSource`
 * is 'manual' — it has to hold over a pick made earlier, possibly on
 * another continent. Silent on failure (offline, endpoint down): the
 * stored language stands and nothing is locked.
 */
function useGeoLanguageBootstrap() {
  const applyGeo = usePrefsStore((s) => s.applyGeo)

  useEffect(() => {
    let cancelled = false
    fetchGeoLocale()
      .then((geo) => {
        if (!cancelled) applyGeo(geo)
      })
      .catch(() => {})
    return () => {
      cancelled = true
    }
    // Only the very first mount probes the network. `applyGeo` re-reads the
    // live preferences when the answer lands, so a switch made in Settings
    // while the request was in flight is never undone by it.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])
}

/**
 * Gate for signed-in routes.
 *
 * While the stored token is still being checked the gate renders a spinner
 * rather than redirecting: bouncing to /auth during that moment would log a
 * returning user out on every page refresh.
 */
function ProtectedRoute({ children }) {
  const status = useAuthStore((s) => s.status)
  const location = useLocation()
  const { t } = useTranslation('common')

  if (status === 'loading') return <Spinner label={t('checkingSession')} />
  if (status === 'anonymous') {
    // Remember where they were headed so sign-in can return them there.
    return <Navigate to="/auth" replace state={{ from: location.pathname + location.search }} />
  }
  return children
}

/**
 * Sign-in, behind the first-run carousel (design page 1): a learner who
 * has never seen it is sent there once — skipping counts — and comes back
 * here with wherever they were headed still attached.
 */
function AuthEntry() {
  const status = useAuthStore((s) => s.status)
  const location = useLocation()
  // The website has no first-run carousel: signing in there is an account
  // matter, not the start of practice (lib/siteMode.js).
  if (isSiteMode()) {
    if (status === 'authenticated') return <Navigate to={postLoginDestination(location.state?.from)} replace />
    return <Auth />
  }
  const destination = startDestination(status, hasSeenOnboarding())
  if (destination !== '/auth') return <Navigate to={destination} replace state={location.state} />
  return <Auth />
}

/**
 * The two onboarding questions for an account that has not answered them
 * (V3-w, decision Q3): an existing learner is sent to them once after the
 * update, from the first practice screen they open, and may put them off —
 * `hasBeenAsked` then keeps this quiet on that device. Answers already on
 * their way up (`readPendingAnswers`) count as answered.
 */
function OnboardingGate({ children }) {
  const user = useAuthStore((s) => s.user)
  if (shouldAskQuestions(user, { pending: readPendingAnswers(), asked: hasBeenAsked() })) {
    return <Navigate to="/onboarding/questions" replace />
  }
  return children
}

/**
 * «قوانین طوطی» (R13, owner decision 2026-10-13): while the server posts
 * every scored take to the feed (`features.autoPost`), a learner who has not
 * accepted the feed rules (consent version 2) sees them before anything
 * else of the app — a new account right after sign-in, before the
 * onboarding questions; an existing one on the next open. Off, this is a
 * pass-through and reads nothing. A status that cannot be read lets the
 * learner in: the practice page asks again before the first take.
 */
function RulesGate({ children }) {
  const autoPost = useAutoPost()
  const userId = useAuthStore((s) => s.user?.id ?? null)
  const { phase, consentGiven, text, version, busy, failed, load, accept } = useRulesStore()
  const { t } = useTranslation('common')

  useEffect(() => {
    if (autoPost && userId) void load(userId)
  }, [autoPost, userId, load])

  const view = rulesView({ autoPost, phase, consentGiven })
  if (view === 'wait') return <Spinner label={t('checkingSession')} />
  if (view === 'ask') return <RulesScreen text={text} version={version} busy={busy} failed={failed} onAccept={() => void accept()} />
  return children
}

/**
 * One practice per lesson: a lesson opened from another one (back/forward,
 * a link) is a fresh page — its repetitions, its take and its lock start
 * over instead of carrying the previous lesson's count (R13, owner 2026-10-14).
 */
function LessonPage() {
  const { lessonId } = useParams()
  return <Lesson key={lessonId} />
}

/**
 * The practice product — app mode only. In a browser tab these routes
 * resolve to the account hub: the website signs a learner in for the
 * subscription, the profile and support, never into the lessons.
 */
function AppArea({ children }) {
  if (isSiteMode()) return <Navigate to="/account" replace />
  return (
    <ProtectedRoute>
      <RulesGate>
        <OnboardingGate>
          <AppShell>{children}</AppShell>
        </OnboardingGate>
      </RulesGate>
    </ProtectedRoute>
  )
}

/** The account pages, served in both modes: the site's frame in a tab, the app's shell when installed. */
function AccountArea({ children }) {
  return (
    <ProtectedRoute>
      {isSiteMode() ? (
        <SiteShell>
          <div className="mx-auto max-w-4xl px-4 py-6">{children}</div>
        </SiteShell>
      ) : (
        <AppShell>{children}</AppShell>
      )}
    </ProtectedRoute>
  )
}

/**
 * The header bell's badge: the server's unread count (B4), re-read on every
 * route change so a row read on the notifications page — or on the phone —
 * drops off the badge the next time the learner moves. A server that does
 * not answer leaves the last count standing.
 */
function useUnreadCount() {
  const { pathname } = useLocation()
  const [count, setCount] = useState(0)
  useEffect(() => {
    let cancelled = false
    listNotifications()
      .then((data) => {
        if (!cancelled) setCount(data.unreadCount ?? 0)
      })
      .catch(() => {})
    return () => {
      cancelled = true
    }
  }, [pathname])
  return count
}

/**
 * The app's frame (V3-w): a slim header — the mark, the bell, the XP,
 * «خروج» — and `BottomNav`'s floating capsule at the bottom (V8). The main
 * column pads its foot by the capsule's 84px reach plus a gap and the home
 * indicator, so the last card always scrolls clear of it; `ToastBar` sits
 * just above the capsule.
 */
function AppShell({ children }) {
  const { t } = useTranslation('common')
  const user = useAuthStore((s) => s.user)
  const signOut = useAuthStore((s) => s.signOut)
  const unread = useUnreadCount()
  const online = useOnline()
  // «خروج» asks first (design page 16, the logout dialog) — a header button
  // that ends the session on a stray tap is the one thing a shell must not do.
  const [askLogout, setAskLogout] = useState(false)

  return (
    <div className="min-h-dvh">
      {/* V8: the header sits on the page's ground (no white bar over the
          cream) and carries the brand's own colour mark — the logo is
          unchanged — and the name «طوطی», as the installed app is called. */}
      <header className="sticky top-0 z-10 border-b border-line/70 bg-ground/85 backdrop-blur">
        <div className="mx-auto flex max-w-4xl items-center gap-4 px-4 py-3">
          <NavLink to="/dashboard" className="flex items-center gap-2 font-bold text-brand-700">
            <ParrotMark size={28} className="shrink-0" />
            <span>{t('appName')}</span>
          </NavLink>

          <div className="ms-auto flex items-center gap-3">
            <NavLink
              to="/notifications"
              aria-label={unread > 0 ? t('unreadNotifications', { count: faDigits(unread) }) : t('notifications')}
              className={({ isActive }) =>
                `relative grid size-9 place-items-center rounded-lg transition-colors ${
                  isActive ? 'bg-brand-50 text-brand-700' : 'text-slate-600 hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-slate-700'
                }`
              }
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <path d="M18 15V10a6 6 0 0 0-12 0v5l-2 3h16zM10 21h4" />
              </svg>
              {unread > 0 && (
                <span aria-hidden="true" className="absolute -top-0.5 -start-0.5 grid h-4 min-w-4 place-items-center rounded-full bg-bad px-1 text-[10px] font-bold text-white">
                  {faDigits(Math.min(unread, 99))}
                </span>
              )}
            </NavLink>
            {user && (
              <span className="hidden text-xs text-slate-500 dark:text-slate-400 sm:inline">
                {t('xpSuffix', { xp: faDigits(user.xp) })}
              </span>
            )}
            <button
              type="button"
              onClick={() => setAskLogout(true)}
              className="rounded-lg px-3 py-1.5 text-sm text-slate-500 hover:bg-slate-100 dark:text-slate-400 dark:hover:bg-slate-700"
            >
              {t('logout')}
            </button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-4xl px-4 pt-6 pb-[calc(7rem+env(safe-area-inset-bottom))]">
        {/* The banner used to promise that a recording "stays right here",
            which held only for as long as the page stayed alive: a reload,
            or a backgrounded tab the browser discarded, lost the take.
            Since V9-w the take is kept in IndexedDB (lib/outbox.js) until
            the server has it, so the wording says what actually happens now
            — including the part the outbox deliberately does not do, which
            is send it by itself. */}
        {!online && (
          <StatusBanner tone="warn" icon="offline" className="mb-4">
            {t('offlineBanner')}
          </StatusBanner>
        )}
        {children}
      </main>

      <BottomNav />

      {askLogout && (
        <TootiDialog
          spec={logoutDialog()}
          onConfirm={() => {
            setAskLogout(false)
            signOut()
          }}
          onDismiss={() => setAskLogout(false)}
        />
      )}
    </div>
  )
}

export default function App() {
  const bootstrap = useAuthStore((s) => s.bootstrap)
  const status = useAuthStore((s) => s.status)
  const navigate = useNavigate()
  const { pathname } = useLocation()
  // The installed app's launch scene, over everything until it has flown off
  // (components/launch). Its parrot waits on the bar for the session check.
  const [launching, setLaunching] = useState(launchScenePending)

  useEffect(() => {
    void bootstrap()
  }, [bootstrap])

  // XP and the server's switches are re-read when the tab comes back to the front (throttled in the store).
  useEffect(() => watchProfileRefresh(() => useAuthStore.getState().refreshProfile()), [])

  // The access gate (tooti#121): a learner whose trial is over and who pays
  // for nothing is sent to the purchase page by whichever call hits it first.
  useEffect(() => onSubscriptionRequired(() => navigate('/paywall', { replace: true })), [navigate])

  // "auto" theme is a fixed 19:00/07:00 clock rule, not the OS setting — a
  // session left open across either boundary needs to re-check periodically
  // rather than only resolving it once at load.
  useEffect(() => {
    const recompute = () => useThemeStore.getState().recomputeIfAuto()
    const interval = setInterval(recompute, 5 * 60 * 1000)
    return () => clearInterval(interval)
  }, [])

  // The daily reminder rings from here while a tab is open (settings, P-settings).
  useReminderClock()

  useGeoLanguageBootstrap()

  return (
    <>
      {/* Not over the sign-in: it is a fixed, no-scroll screen (auth-frame), and a 45px banner would push its button below the fold. */}
      {pathname !== '/auth' && <InstallPrompt />}
      <ToastBar />
      <Routes>
        {/* Two ways in (lib/siteMode.js): a browser tab is the website — the
            landing, whoever is signed in — and the installed web app is the
            practice product, which opens on the lessons. */}
        <Route
          path="/"
          element={status === 'authenticated' && !isSiteMode() ? <Navigate to="/dashboard" replace /> : <Landing />}
        />
        <Route path="/auth" element={<AuthEntry />} />
        <Route
          path="/onboarding"
          element={
            isSiteMode() ? <Navigate to="/auth" replace /> : status === 'authenticated' ? <Navigate to="/dashboard" replace /> : <Onboarding />
          }
        />
        {/* The signed-in learner's two questions (V3-w): full-screen like the
            carousel, no shell, so `OnboardingGate` cannot loop back here. */}
        <Route
          path="/onboarding/questions"
          element={
            isSiteMode() ? (
              <Navigate to="/account" replace />
            ) : (
              <ProtectedRoute>
                <Onboarding questionsOnly />
              </ProtectedRoute>
            )
          }
        />

        {/* The account pages — the only signed-in pages the website serves. */}
        <Route
          path="/account"
          element={
            <AccountArea>
              <Account />
            </AccountArea>
          }
        />
        <Route
          path="/paywall"
          element={
            <AccountArea>
              <Paywall />
            </AccountArea>
          }
        />
        <Route
          path="/autopay"
          element={
            <AccountArea>
              <Autopay />
            </AccountArea>
          }
        />
        <Route
          path="/profile/edit"
          element={
            <AccountArea>
              <EditProfile />
            </AccountArea>
          }
        />
        {/* The app's profile links into practice screens; the site's version of it is the hub. */}
        <Route
          path="/profile"
          element={
            isSiteMode() ? (
              <Navigate to="/account" replace />
            ) : (
              <AppArea>
                <Profile />
              </AppArea>
            )
          }
        />

        <Route
          path="/dashboard"
          element={
            <AppArea>
              <Home />
            </AppArea>
          }
        />
        {/* The library tab is the staged path since V7-w; the flat
            catalogue it replaced is not gone — it is «همهٔ درس‌ها», one tap
            down, and every old /lessons link still lands on the library. */}
        <Route
          path="/lessons"
          element={
            <AppArea>
              <ArcadePath />
            </AppArea>
          }
        />
        <Route
          path="/lessons/all"
          element={
            <AppArea>
              <Lessons />
            </AppArea>
          }
        />
        <Route
          path="/lesson/:lessonId"
          element={
            <AppArea>
              <LessonPage />
            </AppArea>
          }
        />
        <Route
          path="/social"
          element={
            <AppArea>
              <Social />
            </AppArea>
          }
        />
        <Route
          path="/leaderboard"
          element={
            <AppArea>
              <Leaderboard />
            </AppArea>
          }
        />
        {/* V5-w: a learner's public profile, opened from a name or an avatar in the feed, the leaderboard or the team. */}
        <Route
          path="/u/:userId"
          element={
            <AppArea>
              <PublicProfile />
            </AppArea>
          }
        />
        {/* The progress section lives on the profile since V3-w; the old
            address (the account hub, a notification's link) still lands there. */}
        <Route path="/progress" element={<Navigate to="/profile" replace />} />
        <Route
          path="/notifications"
          element={
            <AppArea>
              <Notifications />
            </AppArea>
          }
        />
        <Route
          path="/shop"
          element={
            <AppArea>
              <Shop />
            </AppArea>
          }
        />
        <Route
          path="/settings"
          element={
            <AppArea>
              <Settings />
            </AppArea>
          }
        />
        <Route
          path="/alerts"
          element={
            <AppArea>
              <Alerts />
            </AppArea>
          }
        />
        <Route
          path="/archive"
          element={
            <AppArea>
              <Archive />
            </AppArea>
          }
        />
        <Route
          path="/archive/:attemptId"
          element={
            <AppArea>
              <ArchiveResult />
            </AppArea>
          }
        />

        <Route path="/admin" element={<Admin />} />
        {/* Public like the landing: a learner deciding how to install has
            no session yet, and one who does can still fetch the APK. */}
        <Route path="/download" element={<Download />} />
        {/* Public: this is the exact URL Google Play's Data Safety form asks
            for, so it must open without a session. */}
        <Route path="/privacy" element={<Privacy />} />
        {/* Public for the same reason (tooti#150): Google Play requires a web
            path to delete an account that works without the app. */}
        <Route path="/delete-account" element={<DeleteAccount />} />
        {/* The site's two company pages (design "Tooti Web"): public like the landing. */}
        {/* About is switched off for now; pages/About.jsx stays for when it returns. */}
        <Route path="/about" element={<Navigate to="/" replace />} />
        <Route path="/contact" element={<Contact />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
      {launching && <LaunchScene ready={status !== 'loading'} onDone={() => setLaunching(false)} />}
    </>
  )
}
